import { hasActiveCapture } from "../buildings";
import { resolveAttack, resolveAttackOnCannon } from "../combat";
import { applyBuild, applyCapture, resetCaptureAt } from "../economy";
import { facingFromPath, facingTowardAttackTarget, horizontalFacingToward } from "../fixtures";
import { findUnit, getTile } from "../grid";
import { isTransport } from "../transport";
import type { Coord, GameEvent, GameState, Move, Unit } from "../types";
import { cargoFromPassenger, combatRecordFields, pathDestination, setUnit } from "./helpers";
import { runJoinMove } from "./join";

export type StepOutcome = { state: GameState; events: GameEvent[] };

function resetCaptureIfLeavingTile({ state, pos }: { state: GameState; pos: Coord }): StepOutcome {
  const tileAtOrigin = getTile(state, pos);
  if (!hasActiveCapture(tileAtOrigin)) return { state, events: [] };
  return resetCaptureAt({ state, pos, reason: "moved" });
}

function runWaitMove(state: GameState, move: Extract<Move, { type: "wait" }>): StepOutcome {
  const unit = findUnit(state, move.unitId);
  const patch: Partial<Unit> = { hasMoved: true, hasAttacked: true };
  if (unit.unloadReady) patch.unloadReady = false;
  return {
    state: setUnit(state, { id: move.unitId, patch }),
    events: [{ type: "waited", unitId: move.unitId }],
  };
}

function runPrepareUnload(
  state: GameState,
  move: Extract<Move, { type: "prepareUnload" }>,
): StepOutcome {
  return {
    state: setUnit(state, { id: move.unitId, patch: { unloadReady: true } }),
    events: [{ type: "unloadPrepared", unitId: move.unitId }],
  };
}

function runUnload(state: GameState, move: Extract<Move, { type: "unload" }>): StepOutcome {
  const transport = findUnit(state, move.unitId);
  const cargo = transport.cargo!;
  const newUnitId = `u${state.nextUnitId}`;
  const spawned: Unit = {
    id: newUnitId,
    owner: transport.owner,
    kind: cargo.kind,
    pos: { ...move.pos },
    hp: cargo.hp,
    hasMoved: true,
    hasAttacked: true,
    facing: horizontalFacingToward({
      from: transport.pos,
      to: move.pos,
      fallback: transport.facing,
    }),
    ...combatRecordFields(cargo),
  };
  return {
    state: {
      ...state,
      nextUnitId: state.nextUnitId + 1,
      units: state.units
        .map((u) => {
          if (u.id !== transport.id) return u;
          const { cargo: _cargo, unloadReady: _unloadReady, ...rest } = u;
          return { ...rest, hasMoved: true, hasAttacked: true };
        })
        .concat(spawned),
    },
    events: [
      {
        type: "unloaded",
        transportId: transport.id,
        unitId: newUnitId,
        kind: cargo.kind,
        pos: move.pos,
      },
    ],
  };
}

function runLoad(state: GameState, move: Extract<Move, { type: "load" }>): StepOutcome {
  const passenger = findUnit(state, move.unitId);
  const transport = findUnit(state, move.transportId);
  const cargo = cargoFromPassenger(passenger);
  const { state: base, events } = resetCaptureIfLeavingTile({ state, pos: passenger.pos });
  const transportPatch: Partial<Unit> = { cargo };
  if (transport.hasMoved) transportPatch.hasAttacked = true;
  return {
    state: {
      ...base,
      units: base.units
        .filter((u) => u.id !== passenger.id)
        .map((u) => (u.id === transport.id ? { ...u, ...transportPatch } : u)),
    },
    events: [
      ...events,
      { type: "moved", unitId: passenger.id, from: passenger.pos, path: move.path },
      {
        type: "loaded",
        unitId: passenger.id,
        transportId: transport.id,
        cargoKind: passenger.kind,
      },
    ],
  };
}

function runTravelMove(state: GameState, move: Extract<Move, { type: "move" }>): StepOutcome {
  const unit = findUnit(state, move.unitId);
  const dest = pathDestination(move.path, unit.pos);
  const facing = facingFromPath({
    path: move.path,
    startPos: unit.pos,
    fallback: unit.facing,
  });
  const { state: base, events: captureResetEvents } = resetCaptureIfLeavingTile({
    state,
    pos: unit.pos,
  });
  const patch: Partial<Unit> = { pos: dest, hasMoved: true, facing };
  if (unit.unloadReady) patch.unloadReady = false;
  if (isTransport(unit.kind) && !unit.cargo) patch.hasAttacked = true;
  const next = setUnit(base, {
    id: unit.id,
    patch,
  });
  return {
    state: next,
    events: [
      ...captureResetEvents,
      { type: "moved", unitId: unit.id, from: unit.pos, path: move.path },
    ],
  };
}

function resolveAttackAfterPositioning({
  state,
  move,
}: {
  state: GameState;
  move: Extract<Move, { type: "attack" }>;
}): StepOutcome {
  if ("targetPos" in move && move.targetPos) {
    return resolveAttackOnCannon(state, {
      attackerId: move.unitId,
      targetPos: move.targetPos,
    });
  }
  return resolveAttack(state, {
    attackerId: move.unitId,
    targetId: (move as { targetId: string }).targetId,
  });
}

function positionAttackerThenResolve({
  state,
  move,
}: {
  state: GameState;
  move: Extract<Move, { type: "attack" }>;
}): StepOutcome {
  const unit = findUnit(state, move.unitId);
  const targetPos =
    "targetPos" in move && move.targetPos
      ? move.targetPos
      : findUnit(state, (move as { targetId: string }).targetId).pos;
  const events: GameEvent[] = [];
  let intermediate = state;
  const attackFrom =
    move.path && move.path.length > 0 ? pathDestination(move.path, unit.pos) : unit.pos;
  const facing = facingTowardAttackTarget({
    from: attackFrom,
    to: targetPos,
    path: move.path,
    startPos: unit.pos,
    currentFacing: unit.facing,
  });
  if (move.path && move.path.length > 0) {
    const { state: stateAfterCaptureReset, events: captureResetEvents } = resetCaptureIfLeavingTile(
      {
        state,
        pos: unit.pos,
      },
    );
    intermediate = stateAfterCaptureReset;
    events.push(...captureResetEvents);
    intermediate = setUnit(intermediate, {
      id: unit.id,
      patch: { pos: attackFrom, hasMoved: true, facing },
    });
    events.push({ type: "moved", unitId: unit.id, from: unit.pos, path: move.path });
  } else {
    intermediate = setUnit(intermediate, { id: unit.id, patch: { facing } });
  }
  const result = resolveAttackAfterPositioning({ state: intermediate, move });
  return { state: result.state, events: [...events, ...result.events] };
}

export function executeNonTurnMove(
  state: GameState,
  move: Exclude<Move, { type: "endTurn" }>,
): StepOutcome {
  switch (move.type) {
    case "wait":
      return runWaitMove(state, move);
    case "prepareUnload":
      return runPrepareUnload(state, move);
    case "unload":
      return runUnload(state, move);
    case "load":
      return runLoad(state, move);
    case "capture":
      return applyCapture(state, move.unitId);
    case "build":
      return applyBuild({ state, pos: move.pos, unitKindReq: move.unitKind });
    case "move":
      return runTravelMove(state, move);
    case "attack":
      return positionAttackerThenResolve({ state, move });
    case "join":
      return runJoinMove(state, move);
  }
}
